import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, Loader2, Play } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { QueryKey } from '@/Config/api/queryKey';
import { IndexPath } from '@/Config/app/router/index.path';
import { KnowledgeBase } from '@/Domain/api/KnowledgeBase/dto/KnowledgeBase.dto';
import { RunListDto } from '@/Domain/api/Simulation/dto/Simulation.dto';
import { Usage } from '@/Domain/api/Subscription/dto/SubscriptionUsage.dto';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/Domain/components/Shared/Dialog/Dialog';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { RetrainStateEnum } from '@/Domain/components/TopBar/types';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { TierEnum } from '@/Domain/enum/Tier.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { useGetSubscription } from '@/Domain/hooks/Subscription/useGetSubscription.hook';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  datasetId: number;
  messageCount: number;
  onSuccess: (runId: number) => void;
};

export const StartRunDialog = ({
  open,
  onOpenChange,
  datasetId,
  messageCount,
  onSuccess,
}: Props) => {
  const { t } = useTranslation('simulation_page');
  const { capture } = usePostHogAnalytics();
  const navigate = useNavigate();
  const { scenario = '' } = useParams<{ scenario: string }>();
  const [label, setLabel] = useState('');

  const { data: usage } = useQuery<Usage>({
    queryKey: [QueryKey.subscription.usage],
    enabled: open,
  });
  const { data: subscription } = useGetSubscription();
  const isCustomTier = subscription?.current_plan.tier === TierEnum.CUSTOM;

  const { data: knowledgeBase } = useQuery<KnowledgeBase>({
    queryKey: ['knowledge_base'],
    enabled: open,
  });
  const kbRetrainState = knowledgeBase?.retrain_state ?? RetrainStateEnum.UP_TO_DATE;
  const isKbStale = kbRetrainState !== RetrainStateEnum.UP_TO_DATE;

  const startMutation = useMutation<RunListDto, unknown, { datasetId: number; label?: string }>({
    mutationKey: [QueryKey.simulation.startRun],
  });

  const remaining = usage ? Math.max(0, usage.messages_limit - usage.messages_used) : null;
  const insufficientQuota = !isCustomTier && remaining !== null && remaining < messageCount;

  const handleSubmit = () => {
    startMutation.mutate(
      { datasetId, label: label.trim() || undefined },
      {
        onSuccess: (data) => {
          capture(PostHogEventEnum.SimulationRunStarted, {
            dataset_id: datasetId,
            run_id: data.id,
            message_count: messageCount,
            has_label: label.trim().length > 0,
          });
          if (isKbStale) {
            capture(PostHogEventEnum.SimulationRunStartedOnStaleKb, {
              dataset_id: datasetId,
              run_id: data.id,
              retrain_state: kbRetrainState,
            });
          }
          toast.success(t('toast_run_started'));
          setLabel('');
          onSuccess(data.id);
        },
        onError: () => toast.error(t('toast_run_error')),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('start_run_title')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <Input
            data-testid="start-run-label"
            placeholder={t('run_label_placeholder')}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />

          {isKbStale && (
            <div
              data-testid="start-run-kb-stale"
              className="rounded-md border border-warning/30 bg-warning-muted px-3 py-2 text-sm text-warning-foreground"
            >
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <div>
                  <p>{t('kb_stale_warning')}</p>
                  <button
                    className="mt-1 text-sm font-medium underline"
                    onClick={() => navigate(`/i/${scenario}/${IndexPath.knowledgeBase.root}`)}
                  >
                    {t('kb_stale_retrain_first')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Quota info */}
          {(isCustomTier || remaining !== null) && (
            <div
              data-testid="start-run-quota"
              className={`rounded-md px-3 py-2 text-sm ${
                insufficientQuota
                  ? 'border border-destructive/30 bg-destructive-muted text-destructive'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              {insufficientQuota ? (
                <div className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  <div>
                    <p>
                      {t('quota_exceeded_warning', {
                        needed: messageCount,
                        remaining,
                      })}
                    </p>
                    <button
                      className="mt-1 text-sm font-medium text-destructive underline"
                      onClick={() => navigate(`/i/${scenario}/${IndexPath.subscription}`)}
                    >
                      {t('upgrade_plan')}
                    </button>
                  </div>
                </div>
              ) : isCustomTier ? (
                <p>{t('quota_info_custom', { needed: messageCount })}</p>
              ) : (
                <p>
                  {t('quota_info', {
                    needed: messageCount,
                    remaining,
                  })}
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('cancel')}
          </Button>
          <Button
            data-testid="start-run-submit"
            onClick={handleSubmit}
            disabled={startMutation.isPending || insufficientQuota}
          >
            {startMutation.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            <Play className="mr-1.5 h-3.5 w-3.5" />
            {t('start_run')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
